import { useEffect, useState } from "react";
import { useControllers } from "../state/controllers";
import { useProjectStore } from "../state/projectStore";
import { usePlaybackStore } from "../state/playbackStore";
import { listScores, getScore, deleteScore, type ScoreMeta } from "../state/auth";
import { useT, useLang } from "../i18n";

const LOCALE = { ko: "ko-KR", en: "en-US", ja: "ja-JP" } as const;

const NoteIcon = ({ s = 22 }: { s?: number }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" />
  </svg>
);

export function MyScores({ onClose }: { onClose: () => void }) {
  const c = useControllers();
  const t = useT();
  const lang = useLang((s) => s.lang);
  const fmtDate = (sec: number) =>
    new Date(sec * 1000).toLocaleDateString(LOCALE[lang], { year: "numeric", month: "short", day: "numeric" });
  const [scores, setScores] = useState<ScoreMeta[]>([]);
  const [max, setMax] = useState(3);
  const [busy, setBusy] = useState(true);

  const reload = async () => {
    try { const d = await listScores(); setScores(d.scores); setMax(d.max); } catch { /* ignore */ } finally { setBusy(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const load = async (id: string) => {
    try {
      const { title, data } = await getScore(id);
      c.song.pause(); c.youtube.stop(); c.score.stop();
      useProjectStore.getState().loadProject({
        analysis: data.analysis, source: data.source, title: data.title || title,
        beatLevelMul: data.beatLevelMul ?? 1, arrangement: data.arrangement || { measures: {} }, savedId: id,
      });
      usePlaybackStore.getState().setPlayMode("song");
      if (data.source?.kind === "youtube" && data.source.videoId) await c.youtube.load(data.source.videoId);
      onClose();
    } catch { /* ignore */ }
  };

  const del = async (id: string) => {
    if (!confirm(t("scores.delete_confirm"))) return;
    await deleteScore(id);
    if (useProjectStore.getState().savedId === id) useProjectStore.getState().setSavedId(null);
    reload();
  };

  const atLimit = scores.length >= max;

  return (
    <div className="scores-page">
      <header className="scores-topbar">
        <button className="scores-back" onClick={onClose} aria-label={t("scores.close")}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
        <div className="scores-ttl">{t("scores.title")}</div>
        <div className="scores-count mono">{t("scores.count", { n: scores.length, max })}</div>
      </header>

      <div className="scores-body">
        {busy ? null : scores.length === 0 ? (
          <div className="sc-empty">
            <div className="sc-empty-ic"><NoteIcon s={30} /></div>
            <div>
              <div className="sc-empty-h">{t("scores.empty")}</div>
              <div className="sc-empty-p">{t("scores.empty_sub")}</div>
            </div>
          </div>
        ) : (
          <>
            <ul className="scores-list">
              {scores.map((s) => (
                <li key={s.id} className="sc-card" onClick={() => load(s.id)} role="button" tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); load(s.id); } }}>
                  <div className={"sc-thumb" + (s.video_id ? "" : " ph")}>
                    {s.video_id ? (
                      <>
                        <img src={`https://i.ytimg.com/vi/${s.video_id}/mqdefault.jpg`} alt="" loading="lazy" />
                        <span className="sc-play"><svg width="9" height="9" viewBox="0 0 9 9"><path d="M1.5 1L8 4.5 1.5 8z" fill="#fff" /></svg></span>
                      </>
                    ) : <NoteIcon s={22} />}
                  </div>
                  <div className="sc-mid">
                    <div className="sc-title">{s.title || t("scores.untitled")}</div>
                    <div className="sc-meta">
                      {s.key && <span className="sc-badge">{s.key}</span>}
                      {s.bpm ? <span className="sc-badge"><span className="mono">♩={s.bpm}</span></span> : null}
                      <span className="sc-date">{fmtDate(s.updated_at)}</span>
                    </div>
                  </div>
                  <div className="sc-act">
                    <span className="sc-go" aria-hidden="true">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                    </span>
                    <button className="sc-del" title={t("scores.delete")} onClick={(e) => { e.stopPropagation(); del(s.id); }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            {atLimit && (
              <div className="sc-upgrade">
                <div className="sc-up-ic"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l2.5 6.5L21 9l-5 4 1.5 7L12 16l-5.5 4L8 13 3 9l6.5-.5z" /></svg></div>
                <div className="sc-up-text">
                  <div className="sc-up-h">{t("scores.limit_h")}</div>
                  <div className="sc-up-p">{t("scores.limit_p")}</div>
                </div>
                <button className="sc-up-btn" onClick={() => alert(t("scores.upgrade_soon"))}>{t("scores.upgrade")}</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
